import { useCallback, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Drawer } from '../../../components/ui/Drawer';
import Notification, {
  NotificationContainer,
} from '../../../components/Notification';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import { ThirdPartyBulkImportResult } from '../../../types/compliance';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_EXT = ['.csv', '.xlsx'];

export interface ThirdPartyBulkImportDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Fires after an import that created at least one row so the parent can
   *  refresh the list. */
  onImported: () => void;
}

export default function ThirdPartyBulkImportDrawer({
  open,
  onClose,
  onImported,
}: ThirdPartyBulkImportDrawerProps) {
  const [file, setFile] = useState<File | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ThirdPartyBulkImportResult | null>(null);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  const handleDownloadTemplate = useCallback(async () => {
    setDownloading(true);
    try {
      await thirdPartiesService.downloadImportTemplate();
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Unknown error';
      setErrorNotification({ title: 'Failed to download template', message: msg });
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] || null;
      if (!f) {
        setFile(null);
        return;
      }
      if (f.size > MAX_BYTES) {
        setErrorNotification({
          title: 'File too large',
          message: `Max file size is 5 MB. You selected ${(f.size / 1024 / 1024).toFixed(2)} MB.`,
        });
        e.target.value = '';
        setFile(null);
        return;
      }
      const lower = f.name.toLowerCase();
      if (!ACCEPTED_EXT.some((ext) => lower.endsWith(ext))) {
        setErrorNotification({
          title: 'Unsupported file type',
          message: 'Upload a .csv or .xlsx file. Save .xls as .xlsx first.',
        });
        e.target.value = '';
        setFile(null);
        return;
      }
      setFile(f);
    },
    [],
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const r = await thirdPartiesService.bulkImport(file);
      setResult(r);
      if (r.created > 0) onImported();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail : error?.message || 'Unknown error';
      setErrorNotification({ title: 'Import failed', message: msg });
    } finally {
      setUploading(false);
    }
  }, [file, onImported]);

  const handleClose = useCallback(() => {
    // Reset session state so re-opening starts clean.
    setFile(null);
    setResult(null);
    onClose();
  }, [onClose]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={handleClose}
        disabled={uploading}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
      >
        Close
      </button>
      <button
        type="button"
        onClick={handleUpload}
        disabled={!file || uploading}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Importing...
          </>
        ) : (
          <>
            <CloudArrowUpIcon className="w-4 h-4" /> Upload & Import
          </>
        )}
      </button>
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={open}
        onClose={handleClose}
        title="Bulk Import Third Parties"
        position="left"
        width="40vw"
        footer={footer}
        disableClickOutside
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold mb-1">Step 1 — Download template</h3>
            <p className="text-xs text-muted mb-2">
              Columns: <code>name</code>, <code>legal_entity_name</code>, <code>website</code>.
              Websites without http:// or https:// are auto-prefixed with https://.
            </p>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {downloading ? 'Downloading...' : 'Download CSV Template'}
            </button>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-1">Step 2 — Upload completed file</h3>
            <p className="text-xs text-muted mb-2">
              Accepts <code>.csv</code> or <code>.xlsx</code>. Max 5 MB. Duplicate names
              (case-insensitive) are skipped — existing records are never overwritten.
            </p>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileChange}
              className="block w-full text-sm"
            />
            {file && (
              <p className="text-xs text-gray-500 mt-1">
                Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {result && (
            <div className="space-y-3 mt-4">
              <div className="p-3 border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950 rounded-lg">
                <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">
                  Created {result.created}
                </h4>
                <p className="text-xs text-green-700 dark:text-green-300">
                  {result.created === 0
                    ? 'No new third parties were created.'
                    : `Added ${result.created} new third ${result.created === 1 ? 'party' : 'parties'}.`}
                </p>
              </div>

              {result.skipped.length > 0 && (
                <div className="p-3 border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950 rounded-lg">
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">
                    Skipped {result.skipped.length}
                  </h4>
                  <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 max-h-48 overflow-y-auto">
                    {result.skipped.map((s, i) => (
                      <li key={`${s.row}-${i}`}>
                        <strong>Row {s.row}</strong> · <em>{s.name}</em> — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors.length > 0 && (
                <div className="p-3 border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950 rounded-lg">
                  <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
                    Errors {result.errors.length}
                  </h4>
                  <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 max-h-48 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={`${e.row}-${i}`}>
                        <strong>Row {e.row}</strong>: {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Keyboard escape helper — hidden submit for Enter-submit uniformity */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}>
            <XMarkIcon className="w-0 h-0" />
          </button>
        </div>
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </>
  );
}
